import { useQuery } from '@apollo/client';
import { GetLatestOversightRatingsByDocumentIdsDocument } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useQuery as useReactQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useTRPC } from 'src/utils/trpc';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { isMoreRecentByTestDate } from '@/utils/oversightUtils';

export type OversightDocumentRatingLeaf = {
  Rating: number | null;
  CreatedAt: string | null;
};

export type OversightRatingsByDocument = Map<
  string,
  OversightDocumentRatingLeaf
>;

const EMPTY_MAP: OversightRatingsByDocument = new Map();

/**
 * Fetches the latest oversight rating per document for the given document Ids,
 * keyed by DocumentId.
 *
 * "Latest" mirrors the document page exactly
 * (getLatestOversightAssessmentDocumentAssessmentResultByDocumentId): the
 * oversight result with the newest `TestDate` (tie-broken by
 * `CreatedAtTimestamp`), both read from the result row itself. There is no
 * completion gate — any oversight assessment result counts, so the rating
 * surfaces as soon as it is assigned.
 *
 * Like risks and obligations (and unlike controls), `document_oversight_result`
 * has no direct ParentDocumentId column — a finding can attach to multiple
 * documents via the `parents` array_relationship. `documentParent` is
 * server-filtered to ParentId ∈ $DocumentIds; the client fans the leaf out
 * across each matching parent.
 *
 * Skips the query when oversight viewing is disabled or no documents are
 * visible.
 */
export const useLatestOversightRatingsByDocuments = (params: {
  documentIds: string[];
  isEnabled: boolean;
}): {
  ratingsByDocument: OversightRatingsByDocument;
  loading: boolean;
} => {
  const { documentIds, isEnabled } = params;
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');
  const skip = !isEnabled || documentIds.length === 0;

  const { data, loading } = useQuery(
    GetLatestOversightRatingsByDocumentIdsDocument,
    {
      skip: skip || trpcEnabled,
      variables: { DocumentIds: documentIds },
      fetchPolicy: 'cache-and-network',
    }
  );

  const trpc = useTRPC();
  const { data: trpcData, isLoading: trpcLoading } = useReactQuery({
    ...trpc.frontend.oversight.ratingsByDocumentIds.queryOptions({
      documentIds,
    }),
    enabled: trpcEnabled && !skip,
  });

  const ratingsByDocument = useMemo<OversightRatingsByDocument>(() => {
    if (trpcEnabled) {
      if (!trpcData) {
        return EMPTY_MAP;
      }

      const byDocument: OversightRatingsByDocument = new Map();
      for (const row of trpcData) {
        byDocument.set(row.DocumentId, {
          Rating: row.Rating,
          CreatedAt: row.CreatedAt,
        });
      }

      return byDocument;
    }

    if (!data) {
      return EMPTY_MAP;
    }

    const latestByDocument = new Map<
      string,
      { leaf: OversightDocumentRatingLeaf; TestDate: string | null }
    >();

    for (const row of data.document_oversight_result) {
      const testDate = row.TestDate ?? null;
      const createdAt = row.CreatedAtTimestamp ?? null;
      const leaf: OversightDocumentRatingLeaf = {
        Rating: row.Rating ?? null,
        CreatedAt: createdAt,
      };
      for (const dp of row.documentParent) {
        const existing = latestByDocument.get(dp.ParentId);
        if (
          !existing ||
          isMoreRecentByTestDate(
            {
              TestDate: existing.TestDate,
              CreatedAt: existing.leaf.CreatedAt,
            },
            { TestDate: testDate, CreatedAt: createdAt }
          )
        ) {
          latestByDocument.set(dp.ParentId, { leaf, TestDate: testDate });
        }
      }
    }

    const result: OversightRatingsByDocument = new Map();
    for (const [id, entry] of latestByDocument) {
      result.set(id, entry.leaf);
    }

    return result;
  }, [trpcEnabled, trpcData, data]);

  return {
    ratingsByDocument,
    loading: trpcEnabled ? trpcLoading : loading,
  };
};
